import moment from "moment";
import { getDatePickerDate } from "./DateConverter";

// Whole-calendar-month window helpers (YYYY-MM-DD strings in, YYYY-MM-DD out).
//
// "Today" defaults to getDatePickerDate() -- the calendar day in the ORG's
// timezone -- so a window does not shift for a user whose browser is set to
// another region. Every function takes an explicit anchor for tests.

const DATE_FORMAT = "YYYY-MM-DD";
const MONTH_KEY_FORMAT = "YYYY-MM";

const parse = (value) => {
  if (value == null || value === "") return null;
  const m = moment(value, [DATE_FORMAT, MONTH_KEY_FORMAT], true);
  if (m.isValid()) return m;
  if (typeof value === "string") return null;
  // dayjs / moment / Date objects from a picker.
  const loose = moment(typeof value?.toDate === "function" ? value.toDate() : value);
  return loose.isValid() ? loose : null;
};

/**
 * First and last day of the month a date (or a "YYYY-MM" month key) falls in.
 * @returns {{ startDate: string, endDate: string } | null}
 */
export function getMonthDateRange(value) {
  const m = parse(value);
  if (!m) return null;
  return {
    startDate: m.clone().startOf("month").format(DATE_FORMAT),
    endDate: m.clone().endOf("month").format(DATE_FORMAT),
  };
}

/**
 * The last `months` COMPLETE calendar months, ending with the month before today's.
 * e.g. today 2026-10-09, months 6 -> 2026-04-01 .. 2026-09-30.
 */
export function getDefaultMonthWindow(months = 6, today = getDatePickerDate()) {
  const anchor = parse(today) || moment();
  const count = Math.max(1, Math.floor(months) || 1);
  const end = anchor.clone().startOf("month").subtract(1, "month");
  const start = end.clone().subtract(count - 1, "months");
  return {
    startDate: start.startOf("month").format(DATE_FORMAT),
    endDate: end.endOf("month").format(DATE_FORMAT),
  };
}

/** "YYYY-MM" key of the month after `value`'s month, or null. */
export function getNextMonthKey(value) {
  const m = parse(value);
  return m ? m.clone().startOf("month").add(1, "month").format(MONTH_KEY_FORMAT) : null;
}

/** Display label for a month ("Jul 2026" by default); the raw value when unreadable. */
export function formatMonthLabel(value, format = "MMM YYYY") {
  const m = parse(value);
  return m ? m.format(format) : String(value ?? "");
}
